const SCOPES = 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar';
const CALENDAR_API = 'https://www.googleapis.com/calendar/v3';

let tokenClient = null;
let accessToken = null;
let tokenExpiresAt = 0;

/**
 * Dynamically load the Google Identity Services script
 */
const loadGisScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
};

/**
 * Initialize the GIS token client
 */
const initTokenClient = (promptMode = '') => {
  return new Promise((resolve, reject) => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      reject(new Error('REACT_APP_GOOGLE_CLIENT_ID is not configured'));
      return;
    }

    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      prompt: promptMode,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error));
          return;
        }
        accessToken = response.access_token;
        tokenExpiresAt = Date.now() + (response.expires_in * 1000) - 60000; // 1min buffer
        resolve(response.access_token);
      },
      error_callback: (error) => {
        reject(new Error(error.message || 'Google authorization failed'));
      }
    });

    tokenClient.requestAccessToken();
  });
};

/**
 * Get a valid access token. If no token or expired, triggers GIS consent.
 * @param {boolean} forceConsent - true for first-time connect, false for silent renewal
 */
export const getAccessToken = async (forceConsent = false) => {
  if (accessToken && Date.now() < tokenExpiresAt && !forceConsent) {
    return accessToken;
  }

  await loadGisScript();

  const promptMode = forceConsent ? 'consent' : '';
  return initTokenClient(promptMode);
};

/**
 * Check if we currently have a valid token (without triggering consent)
 */
export const hasValidToken = () => {
  return accessToken && Date.now() < tokenExpiresAt;
};

/**
 * Clear the stored token (for disconnect)
 */
export const clearToken = () => {
  if (accessToken && window.google?.accounts?.oauth2) {
    window.google.accounts.oauth2.revoke(accessToken);
  }
  accessToken = null;
  tokenExpiresAt = 0;
  tokenClient = null;
};

/**
 * Make an authenticated API call with automatic token retry
 */
const apiCall = async (url, options = {}) => {
  let token = await getAccessToken();

  const doFetch = async (t) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${t}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (res.status === 401) {
      // Token expired — get a fresh one
      token = await getAccessToken(false);
      const retry = await fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({}));
        throw new Error(err.error?.message || `API error: ${retry.status}`);
      }
      return retry;
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `API error: ${res.status}`);
    }

    return res;
  };

  return doFetch(token);
};

/**
 * Find the "coparent" calendar or create it
 */
export const findOrCreateCoparentCalendar = async () => {
  // List existing calendars
  const listRes = await apiCall(`${CALENDAR_API}/users/me/calendarList`);
  const { items } = await listRes.json();

  const existing = items?.find(cal => cal.summary === 'coparent');
  if (existing) return existing.id;

  // Create new calendar
  const createRes = await apiCall(`${CALENDAR_API}/calendars`, {
    method: 'POST',
    body: JSON.stringify({
      summary: 'coparent',
      description: 'Custody schedule and activities synced from coparent',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  });

  const calendar = await createRes.json();
  return calendar.id;
};

/**
 * Create a Google Calendar event
 */
export const createEvent = async (calendarId, eventData) => {
  const res = await apiCall(`${CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events`, {
    method: 'POST',
    body: JSON.stringify(eventData),
  });
  return res.json();
};

/**
 * Update a Google Calendar event
 */
export const updateEvent = async (calendarId, eventId, eventData) => {
  const res = await apiCall(
    `${CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(eventData),
    }
  );
  return res.json();
};

/**
 * Delete a Google Calendar event
 */
export const deleteEvent = async (calendarId, eventId) => {
  await apiCall(
    `${CALENDAR_API}/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`,
    { method: 'DELETE' }
  );
};

/**
 * Delete multiple events (best-effort, doesn't throw on individual failures)
 */
export const batchDeleteEvents = async (calendarId, eventIds) => {
  const results = await Promise.allSettled(
    eventIds.map(id => deleteEvent(calendarId, id))
  );
  const failed = results.filter(r => r.status === 'rejected');
  if (failed.length > 0) {
    console.warn(`Failed to delete ${failed.length}/${eventIds.length} events`);
  }
};
